import {
    GetAreasResponse,
    GetLocationsBaseResponse,
    GetLocationsResponse,
    GetMediaResponse,
    GetUniverseResponse,
    LoginRequest,
    LoginResponse,
    RegisterRequest,
    RegisterResponse,
    CreateProfileRequest,
    EditProfileRequest,
    GetProfilesResponse,
    SetUserAssetsResponse,
    EditProfileTitleRequest,
    EditProfileTitleResponse,
    TokenExchangeResponse,
    CommandResponse,
    CommandRequest,
    SetFactionRequest,
    SetFactionResponse,
} from '@/arise/api/types';
import { InventoryItem } from '@/models/Inventory';
import { User } from '@/models/auth';
import { NewsTickerDataResponse, Reaction, ReactionUpdatePayload } from '@/models/feed';
import { TranslationsResponse } from '@/models/translations';
import {
    AvatarFrame,
    Background,
    Badge,
    BanReason,
    Character,
    Faction,
    FactionSettings,
    ProfileAssetType,
    ProfileV2,
    Title,
    UserOwnedAssets,
} from '@/models/profile';
import {
    ArisewareScore,
    ClaimRewardResponse,
    RewardStatusResponse,
    GenericLeaderboardData,
    ModalData,
    PollData,
    Reward,
    SupplyRunLeaderboardData,
    SupplyRunUserStats,
} from '@/models/misc';
import { AssetPack, CheckoutResponse, ExternalShopItem, SessionStatusResponse, ShopItemTypes } from '@/models/shop';
import { SceneSelectorSettings } from '@/models/misc';
import { parsePayloadURLS, pathToURL } from '@/utils/urls';
import { processLocationData } from '@/utils/location';
import { processProfileData, processTitles } from '@/utils/profile';
import { getTokenPayload, isJWTFormat, manageTokenExpiry } from '@/utils/auth';
import { getLocalUserTitles, setLocalUserTitles } from '@/utils/localTitles';
import { FEED_MESSAGES_TO_LOAD, IS_DISCORD_ACTIVITY } from '@/globals';
import { getProfileV2FromLocalStorage } from '@/utils/localProfileV2';

const API_HOST = IS_DISCORD_ACTIVITY ? '/.proxy/api' : process.env.PUBLIC_ARISEENGINE_HOST || 'http://localhost:3000';
const STATIC_DATA_HOST = IS_DISCORD_ACTIVITY ? '/.proxy/static-data' : process.env.PUBLIC_STATIC_DATA_HOST;
const LOCAL_FILE_PREFIX = IS_DISCORD_ACTIVITY ? '/.proxy' : '';
const AUTHTOKEN_KEY = 'ARISEENGINE_AUTHTOKEN';

let authToken = '';
let currentUserId = '';
let universeID = window.localStorage.getItem('ARISE_UNIVERSEID') || process.env.PUBLIC_INSTANCE_ID;

manageTokenExpiry();

if (typeof window !== 'undefined') {
    authToken = window.localStorage.getItem(AUTHTOKEN_KEY) || '';
    setCurrentUserId();
}

export function setAuthToken(token: string) {
    if (!token || !isJWTFormat(token)) {
        console.warn('Tried to call setAuthToken with an invalid or incorrectly formatted token:', token);
        return;
    }
    authToken = token;
    window.localStorage.setItem(AUTHTOKEN_KEY, token);
    // manageTokenExpiry();
    setCurrentUserId();
}

function setCurrentUserId() {
    try {
        const data = getTokenPayload(authToken);
        currentUserId = data.sub || '';
    } catch (error) {}
}

export function getAuthToken() {
    return authToken;
}

export function setUniverseID(id: string) {
    universeID = id;
}

export async function register(email: string, password: string, turnstileToken: string) {
    const body: RegisterRequest = {
        email: email,
        password: password,
        turnstileToken: turnstileToken,
    };

    return await call<RegisterResponse>('/register', 'POST', body);
}

export async function createProfile(body: CreateProfileRequest) {
    return await call<User>('/profile/new', 'POST', body);
}

export async function editProfile(profileId: string, body: EditProfileRequest) {
    return await call<User>(`/profile/${profileId}`, 'PUT', body);
}

export async function setUserCurrentAsset(assetType: ProfileAssetType, assetID: string) {
    const body: any = {
        universeID,
        assetID,
        assetType,
    };

    return await call<SetUserAssetsResponse>(`/userCurrentAssets`, 'POST', body);
}

export async function unsetUserCurrentAsset(assetType: ProfileAssetType) {
    const body: any = {
        universeID,
        assetType,
    };

    return await call<SetUserAssetsResponse>(`/userCurrentAssets`, 'DELETE', body);
}

export async function addProfileTitle(id: string) {
    const body: EditProfileTitleRequest = {
        id,
    };
    if (authToken) {
        return await call<EditProfileTitleResponse>('/profileTitle/add', 'POST', body);
    }
}

export async function addProfileBadge(rank: string) {
    const body = {
        rank,
    };
    if (authToken) {
        return await call<EditProfileTitleResponse>('/profileBadge/add', 'POST', body);
    }
}

export async function setProfileTitle(id: string) {
    const body: EditProfileTitleRequest = {
        id,
    };
    return await call<EditProfileTitleResponse>('/profileTitle/set', 'PUT', body);
}

export async function setProfileFaction(factionID: string) {
    const body: SetFactionRequest = {
        factionID,
    };
    return await call<SetFactionResponse>('/userFaction', 'PUT', body);
}

export async function login(email: string, password: string) {
    const body: LoginRequest = {
        email: email,
        password: password,
    };

    const res = await call<LoginResponse>('/login', 'POST', body);
    setAuthToken(res.authToken);
}

export async function loginAsGuest() {
    const res = await call<LoginResponse>('/login/guest', 'POST');
    setAuthToken(res.authToken);
}

export async function oAuthExchange(token: string) {
    const res = await call<LoginResponse>(`/auth/oauth/exchange`, 'POST', {
        token,
    });
    setAuthToken(res.authToken);
}

export async function passwordReset(email: string) {
    await call<LoginResponse>(`/auth/password-reset`, 'POST', {
        email,
    });
}

export async function passwordChange(email: string, token: string, password: string) {
    await call<LoginResponse>(`/auth/password-change`, 'POST', {
        email,
        token,
        password,
    });
}

export async function me(): Promise<User> {
    try {
        const res = await call<User>('/me', 'GET');

        return res;
    } catch (error) {
        throw error;
    }
}

async function call<T>(path: string, method: string, body?: any, apiHostOverride?: string) {
    const apiHost = apiHostOverride ?? API_HOST;
    const additionalHeaders: Record<string, string> = {};
    let response = await fetch(`${apiHost}${path}`, {
        method: method,
        body: body ? JSON.stringify(body) : undefined,
        headers: {
            'Content-Type': 'application/json',
            'X-Auth-Token': authToken,
            'X-Universe-ID': universeID,
            ...additionalHeaders,
        },
    });

    if (!response.ok) {
        // Try to parse the error code from the response body
        let errorMessage = 'UNKNOWN_ERROR'; // Fallback
        try {
            const errorBody = await response.json();
            errorMessage = errorBody.errorCode ?? 'UNKNOWN_ERROR';
        } catch {}
        throw new Error(errorMessage);
    }

    return (await response.json()) as T;
}

async function callStatic<T>(path: string) {
    try {
        let response = await fetch(`${STATIC_DATA_HOST}/${universeID}-${path}`, {
            method: 'GET',
        });

        if (!response.ok) {
            throw new Error('UNKNOWN_ERROR');
        }

        return (await response.json()) as T;
    } catch (error) {
        console.log(`Static data missing or invalid: /staticData/${path}`);
        throw new Error('UNKNOWN_ERROR');
    }
}

async function callPath<T>(path: string) {
    try {
        let response = await fetch(`${LOCAL_FILE_PREFIX}/${path}`, {
            method: 'GET',
        });

        if (!response.ok) {
            throw new Error('UNKNOWN_ERROR');
        }

        return (await response.json()) as T;
    } catch (error) {
        console.error(`Could not call path ${path}`);
        throw new Error('UNKNOWN_ERROR');
    }
}

function sendBeaconWithFallback(path: string, data: Record<string, any>, apiHostOverride?: string) {
    const apiHost = apiHostOverride ?? API_HOST;
    const url = `${apiHost}${path}`;
    const payload = JSON.stringify(data);

    try {
        if (!navigator?.sendBeacon) {
            throw new Error('sendBeacon not supported');
        }

        const ok = navigator.sendBeacon(url, payload);
        if (!ok) {
            throw new Error('sendBeacon failed');
        }
    } catch {
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'text/plain', // Matches sendBeacon's default
            },
            body: payload,
            keepalive: true, // Allows sending during unload
        }).catch(() => {
            // Fail silently
        });
    }
}
// Make a request with a maximum timeout and default value
const withTimeout = async <T>(request: Promise<T>, timeoutMs: number, defaultValue: T): Promise<T> => {
    const timeout = new Promise<T>((resolve) =>
        setTimeout(() => {
            resolve(defaultValue);
        }, timeoutMs),
    );

    try {
        return await Promise.race([request, timeout]);
    } catch {
        return defaultValue;
    }
};

export async function getAreas(limit: number = 100) {
    return await call<GetAreasResponse>(`/area/list?universeID=${universeID}&limit=${limit}`, 'GET');
}

export async function getLocations() {
    let locations = await callStatic<GetLocationsResponse>('locations.json');

    locations = parsePayloadURLS(locations);

    return locations.map((location) => processLocationData(location));
}

export async function getRegions(limit: number = 100) {
    return await call<GetLocationsBaseResponse>(`/region/list?universeID=${universeID}&limit=${limit}`, 'GET');
}

export async function getUserOwnedAssets() {
    return await call<UserOwnedAssets>(`/userOwnedAssets`, 'GET');
}

export async function getBackgrounds() {
    return await call<Background[]>(`/background/list`, 'GET');
}

export async function getCharacters() {
    return await call<Character[]>(`/character/list`, 'GET');
}

export async function getAvatarFrames() {
    return await call<AvatarFrame[]>(`/avatarFrame/list`, 'GET');
}

export async function getFactions() {
    return await call<Faction[]>(`/faction/list`, 'GET');
}

export async function getFactionSettings() {
    return await call<FactionSettings>(`/factionSettings/list`, 'GET');
}

export async function getSceneSelectorSettings() {
    const res = await call<SceneSelectorSettings>(`/sceneSelectorSettings/list`, 'GET');
    return parsePayloadURLS(res);
}

export async function getProfiles(userIds: string[]) {
    const params = userIds.map((userId) => `id=${userId}`).join('&');
    const profiles = await call<GetProfilesResponse>(`/profile/list?${params}`, 'GET');

    return profiles.map((profile) => processProfileData(profile));
}

export async function getTitles(userId: string, forceUseRemoteData?: boolean) {
    // Legacy
    const localTitles = await getLocalUserTitles();
    if (localTitles.length && !forceUseRemoteData) {
        return localTitles;
    } else {
        const id = userId || currentUserId; // If no user id passed in, try use local currentUserId
        if (!id) return localTitles;
        // If there are no local titles or forceUseRemoteData is true, try get remote titles
        const remoteTitles = await withTimeout(call<Title[]>(`/profile/title/history/${id}`, 'GET'), 5000, []);
        if (Array.isArray(remoteTitles) && remoteTitles.length) {
            setLocalUserTitles(remoteTitles);
            return remoteTitles;
        }
        // If remote is empty or failed, return local
        return localTitles;
    }
}

export async function getCurrentUserProfileV2() {
    // If localstorage version return that, otherwise return remote profile
    const localProfile = getProfileV2FromLocalStorage();
    if (localProfile) return localProfile;
    if (currentUserId) {
        const remoteResponse = await withTimeout(
            call<{ profile: ProfileV2 }>(`/profiles/${currentUserId}`, 'GET'),
            5000,
            null,
        );
        return remoteResponse.profile;
    }
}

export async function getAvailableTitles() {
    const result = await callStatic<Title[]>('titleList.json');
    return processTitles(result);
}

export async function getAvailableBadges() {
    return await callStatic<Badge[]>('badges.json');
}

export async function setProfileMissionCompleted() {
    return await call<any>(`/profile/mission-completed`, 'POST');
}

export async function resetProfileMissionCompleted() {
    return await call<any>(`/profile/reset-mission-completed`, 'POST');
}

export async function resetProfileBadges() {
    return await call<any>(`/profile/reset-badges`, 'POST');
}

export async function resetProfileTitles() {
    return await call<any>(`/profile/reset-titles`, 'POST');
}

export async function logout() {
    // TODO: call logout endpoint
    setAuthToken('');
}

export async function lastChats(
    locationId: string,
    from?: number,
    num?: number,
    isGlobalChat?: boolean,
    isLatest?: boolean,
) {
    // time is a unix timestamp in seconds, or 'latest' if first call
    const time = isLatest ? 'latest' : from || Math.ceil(Date.now() / 1000);

    num = num || FEED_MESSAGES_TO_LOAD;

    const messages = await call<any[]>(
        `/chat/lastChats?locationID=${locationId}&time=${time}&num=${num}${isGlobalChat ? '&globalchat=true' : ''}`,
        'GET',
    );

    return messages.map((message) => {
        if (message.data.profile) {
            message.data.profile = processProfileData(message.data.profile);
        }
        return message;
    });
}

// TODO: chat message types
export async function sendMessage(locationId: string, message: string) {
    const body = {
        locationID: locationId,
        chatText: message,
    };

    return await call<any>(`/chat`, 'POST', body);
}

export async function sendCommand(locationId: string, command: string) {
    const body: CommandRequest = {
        environmentID: locationId,
        command,
    };

    return await call<CommandResponse>(`/command`, 'POST', body);
}

// No longer used
// export async function getMedia(universeId: string) {
//     const res = await callStatic<GetMediaResponse>('universeMedia.json');
//     return parsePayloadURLS(res);
// }

export async function getUniverseMetadata(universeId: string = universeID) {
    const res = await callStatic<GetUniverseResponse>('universe.json');
    return parsePayloadURLS(res);
}

export async function getInventory() {
    const response = await call<any[]>(`/inventory`, 'GET');
    const inventory: InventoryItem[] = response.map((d) => {
        return {
            id: d.id,
            itemId: d.item.id,
            name: d.item.name,
            description: d.item.description,
            inventoryThumbnailUrl: d.item.imagePath ? pathToURL(d.item.imagePath) : d.item.imageURL,
            customJson: d.item.customJson,
        };
    });
    return inventory;
}

export async function takeItem(itemId: string) {
    return call<any>(`/inventory`, 'POST', {
        universeID,
        itemID: itemId,
    });
}

export async function dropItem(inventoryItemId: string) {
    return call<any>(`/inventory/${inventoryItemId}`, 'DELETE');
}

export async function toggleMessageReaction(
    messageId: string,
    reaction: string,
    locationId: string,
): Promise<ReactionUpdatePayload> {
    return await call<ReactionUpdatePayload>(`/reaction/toggle`, 'POST', {
        chatID: messageId,
        reaction,
        environmentID: locationId,
    });
}

export async function listReactionTypes(): Promise<Reaction[]> {
    return await call<Reaction[]>(`/reactions/list`, 'GET');
}

export async function deleteMessage(messageId: string) {
    return await call<any>(`/moderationActions/removeChat`, 'PUT', {
        ChatID: messageId,
    });
}

export async function banUser(userID: string, seconds: number, reason: BanReason) {
    const bannedUntil = new Date(Date.now() + seconds * 1000).toISOString();
    return await call<any>(`/moderationActions/ban`, 'POST', {
        userID,
        bannedUntil,
        reason,
    });
}

export async function getArisewareState(projectId: string) {
    return await call<any>(`/ariseware/state/${projectId}`, 'GET');
}

export async function updateArisewareState(projectId: string, data: any) {
    return await call<any>(`/ariseware/state/${projectId}`, 'PUT', data);
}

// TODO: this endpoint does not exist at the moment, just putting this here for when we have it ready
export async function failedAriseware(projectId: string) {
    // TODO: remove
    return new Promise((resolve) => setTimeout(resolve, 1000));
    // return await call<any>(`/ariseware/failed/${projectId}`, 'POST');
}

export async function getArisewareScoresForUser(userId: string) {
    return await call<ArisewareScore[]>(`/arisewareScore/${userId}`, 'GET');
}

export async function submitArisewareScore(data: any) {
    // Legacy
    return await call<any[]>(`/arisewareScore`, 'PUT', data);
}

export async function submitFeedUpdate(locationId: string, id: string) {
    return await call<any>(`/feedUpdate`, 'POST', {
        environmentID: locationId,
        id,
    });
}

export async function searchByDisplayName(query: string) {
    return await call<any>(`/profile/searchByDisplayName?query=${encodeURIComponent(query)}`, 'GET');
}

export async function getEventById(eventID: string) {
    return await call<any>(`/chatEvent?eventID=${eventID}`, 'GET');
}

export async function tokenExchange(token: string, source: string) {
    const res = await call<TokenExchangeResponse>(`/auth/tokenExchange`, 'POST', {
        token,
        source,
    });

    setAuthToken(res.ariseToken);
    return res;
}

let _heartbeat_has_joined = false;
export async function heartbeat(locationId: string) {
    if (!authToken) return;
    try {
        await call<any>('/presence/heartbeat', 'POST', {
            environmentId: locationId,
            ...(!_heartbeat_has_joined && { joinedLocation: true }),
        });
        _heartbeat_has_joined = true;
    } catch (error) {
        console.warn('Failed to send heartbeat', error);
    }
}

export async function scroll(data: any) {
    return await call<any>(`/scroll`, 'POST', data);
}

export async function scrollV2(data: any, useBeacon?: boolean) {
    const PATH = '/scrollv2';
    if (useBeacon) {
        sendBeaconWithFallback(PATH, data);
    } else {
        return await call<any>(PATH, 'POST', data);
    }
}

export function getInstanceData() {
    return {
        universeID,
        userAuthToken: authToken,
        apiHost: API_HOST,
    };
}

// Shop APIs
export async function getAssetPacks() {
    return await call<AssetPack[]>(`/assetpack/list`, 'GET');
}

export async function getShopExternalItems() {
    return await call<ExternalShopItem[]>(`/shop/external`, 'GET');
}

export async function checkoutShopItem(itemID: string, type: ShopItemTypes) {
    let body = {
        items: [
            {
                type,
                id: itemID,
            },
        ],
        redirectURL: window.location.href + '?checkout=true',
    };
    return await call<CheckoutResponse>('/shop/checkout', 'POST', body);
}

export async function getSessionStatus(id: string) {
    return await call<SessionStatusResponse>(`/shop/checkout/${id}`, 'GET');
}

export async function getMyRewards(): Promise<Reward[]> {
    const response = await call<{ rewards: Reward[] }>(`/rewards`, 'GET');
    return response.rewards;
}

export async function claimReward(promotionId: string): Promise<ClaimRewardResponse> {
    return await call<ClaimRewardResponse>(`/rewards/${promotionId}/claim`, 'POST');
}

export async function getRewardStatus(promotionId: string): Promise<RewardStatusResponse> {
    return await call<RewardStatusResponse>(`/rewards/${promotionId}/status`, 'GET');
}

// Modal API
export async function getNewsModal() {
    return await call<ModalData[]>(`/modal/list`, 'GET');
}

// Supply run APIs
const SUPPLY_RUN_API_HOST = process.env.PUBLIC_SUPPLY_RUN_HOST;

export async function getSupplyRunLeaderboardData() {
    return await call<SupplyRunLeaderboardData>(`/supply-run/stats`, 'GET', null, SUPPLY_RUN_API_HOST);
}

export async function getSupplyRunUserProfileStats(userID: string) {
    return await call<SupplyRunUserStats>(
        `/supply-run/user-profile-stats?userId=${userID}`,
        'GET',
        null,
        SUPPLY_RUN_API_HOST,
    );
}

// News ticker
export async function getNewsTickerData() {
    return await callStatic<NewsTickerDataResponse>('newsTicker.json');
}

export async function getTranslations(): Promise<TranslationsResponse> {
    const response = parsePayloadURLS(await callStatic<TranslationsResponse>('translations.json'));
    return response;
}

export async function userEvent(environmentID: string, eventType: string) {
    if (authToken) {
        const body = {
            environmentID,
            eventType,
        };
        return await call<RegisterResponse>('/userEvents', 'POST', body);
    }
}

// Polls / voting
export async function getPollData(id: string, numberOfResults: number = 5) {
    try {
        const res = await call<PollData>(`/polls/${id}?numberOfResults=${numberOfResults}`, 'GET');
        return res;
    } catch (error) {
        console.error('Failed to fetch poll data', error);
        return fetchStaticData<PollData>(`/${id}.json`);
    }
}

export async function submitPollVote(id: string, selectedOptionIds: string[], numberOfResults: number = 5) {
    const body = {
        pollId: id,
        selectedOptionIds,
    };
    return await call<PollData>(`/polls/${id}/votes?numberOfResults=${numberOfResults}`, 'POST', body);
}

async function fetchStaticData<T>(path: string) {
    const data = await fetch(STATIC_DATA_HOST + path)
        .then((res) => res.json())
        .catch(() => null);

    return data as T;
}

// Generic leaderboard
export async function getGenericLeaderboardData() {
    return await call<GenericLeaderboardData>('/leaderboards', 'GET');
}

export async function submitScore(score: number) {
    const body = {
        score,
    };
    return await call<PollData>('/leaderboards/submit', 'POST', body);
}
